import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, CheckCheck, XCircle, Loader2, Inbox, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PendingActionCard, { canExecutePendingAction } from './PendingActionCard';
import usePendingActions from '../../hooks/usePendingActions';
import { useAuth } from '../../context/AuthContext';
import { readAccess } from '../../constants/featureAccess';
import { PERM } from '../../constants/permissions';
import { BULK_APPROVABLE_TYPES } from '../../utils/calebWorkspace';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'execution_unknown', label: 'Needs Review' },
  { value: 'executed', label: 'Executed' },
  { value: 'rejected', label: 'Rejected' },
  { value: null, label: 'All' },
];

/**
 * PendingActionsPanel — dropdown panel showing Caleb's proposed actions
 * that need user approval. Renders as an icon button in the nav bar
 * with a count badge, similar to NotificationCenter.
 *
 * @param {object} props
 * @param {object} props.user - Current user object from AuthContext.
 */
const PendingActionsPanel = ({ user, triggerClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);

  // Pending actions are Caleb proposals — only surface for users who can use Caleb.
  const { hasAnyPermission, hasPermission } = useAuth();
  const canUse = hasAnyPermission(readAccess('assistant'));
  const canManage = hasPermission(PERM.ASSISTANT_MANAGE);
  useEffect(() => {
    const open = () => { if (canUse) setIsOpen(true); };
    window.addEventListener('caleb:open-action-queue', open);
    return () => window.removeEventListener('caleb:open-action-queue', open);
  }, [canUse]);

  const {
    actions,
    pendingCount,
    loading,
    fetchActions,
    approve,
    reject,
    bulkApprove,
    bulkReject,
    updateParameters,
    resolveExecution,
  } = usePendingActions({
    enabled: !!user && canUse,
    actionsEnabled: isOpen,
    status: statusFilter,
  });
  const manageablePendingActions = actions.filter((action) => (
    action.status === 'pending'
    && canExecutePendingAction(action.action_type, hasAnyPermission)
  ));

  // Clear bulk selection when the panel opens or the query filter changes.
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen, statusFilter]);

  // Toggle selection
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Select all visible pending
  const selectAll = () => {
    const pendingIds = manageablePendingActions.map((action) => action.id);
    setSelectedIds(new Set(pendingIds));
  };

  const deselectAll = () => setSelectedIds(new Set());

  // Bulk handlers
  const handleBulkApprove = async () => {
    const typeById = new Map(actions.map((action) => [action.id, action.action_type]));
    const selected = [...selectedIds];
    const ids = selected.filter((id) => BULK_APPROVABLE_TYPES.has(typeById.get(id)));
    const individual = selected.length - ids.length;
    if (individual > 0) {
      toast.info(`${individual} selected action${individual === 1 ? ' needs' : 's need'} individual review`);
    }
    if (ids.length === 0 || bulkAction) return;
    setBulkAction('approve');
    try {
      await bulkApprove(ids);
      setSelectedIds(new Set());
      await fetchActions();
    } catch {
      // The mutation hook reports the API error and selection remains available.
    } finally {
      setBulkAction(null);
    }
  };

  const handleBulkReject = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0 || bulkAction) return;
    setBulkAction('reject');
    try {
      await bulkReject(ids);
      setSelectedIds(new Set());
      await fetchActions();
    } catch {
      // The mutation hook reports the API error and selection remains available.
    } finally {
      setBulkAction(null);
    }
  };

  // Single action handlers — refetch after to stay synced
  const handleApprove = async (actionId) => {
    await approve(actionId);
  };

  const handleReject = async (actionId) => {
    await reject(actionId);
  };

  if (!canUse) return null;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative ${triggerClassName || 'text-foreground hover:text-foreground dark:hover:text-white'}`}
        title="AI Action Queue"
      >
        <ShieldCheck className="h-5 w-5" />
        {pendingCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-amber-500 text-white text-xs">
            {pendingCount > 9 ? '9+' : pendingCount}
          </Badge>
        )}
      </Button>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Panel */}
          <div className="absolute right-0 mt-2 w-[420px] bg-card rounded-lg shadow-xl z-50 border border-border max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-amber-500" />
                <h3 className="font-semibold text-foreground">AI Action Queue</h3>
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {pendingCount}
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                <span className="sr-only">Close</span>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-1 px-4 py-2 border-b dark:border-gray-700 bg-muted/40">
              <Filter className="h-3.5 w-3.5 text-muted-foreground mr-1" />
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.label}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    statusFilter === filter.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Bulk action bar */}
            {canManage && selectedIds.size > 0 && (
              <div className="flex items-center justify-between px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b dark:border-gray-700">
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  {selectedIds.size} selected
                </span>
                <div className="flex gap-2">
                  <Button size="xs" variant="ghost" onClick={deselectAll}>
                    Clear
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                    onClick={handleBulkReject}
                    disabled={!!bulkAction}
                  >
                    {bulkAction === 'reject'
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <XCircle className="h-3 w-3 mr-1" />}
                    Reject All
                  </Button>
                  <Button
                    size="xs"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleBulkApprove}
                    disabled={!!bulkAction}
                  >
                    {bulkAction === 'approve'
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <CheckCheck className="h-3 w-3 mr-1" />}
                    Approve All
                  </Button>
                </div>
              </div>
            )}

            {/* Select-all helper */}
            {canManage && statusFilter === 'pending' && manageablePendingActions.length > 0 && selectedIds.size === 0 && (
              <div className="px-4 py-1.5 border-b dark:border-gray-700">
                <button
                  onClick={selectAll}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Select all {manageablePendingActions.length} actions
                </button>
              </div>
            )}

            {/* Action list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
                </div>
              ) : actions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Inbox className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-sm font-medium text-foreground">No actions here</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {statusFilter === 'pending' || statusFilter === null
                      ? "When Caleb proposes actions, they'll appear here for your review."
                      : `No ${statusFilter} actions found.`}
                  </p>
                </div>
              ) : (
                actions.map((action) => (
                  <PendingActionCard
                    key={action.id}
                    action={action}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onUpdateParameters={updateParameters}
                    onResolveExecution={resolveExecution}
                    selectable={canManage && statusFilter === 'pending'}
                    selected={selectedIds.has(action.id)}
                    onToggle={toggleSelect}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PendingActionsPanel;
